// Where the background image sits on fcb_matchday_slider_2.
//
// Every other template treats its background layers as full bleed: the layer
// box IS the canvas, so the editor can draw them straight into a 100%/100% div
// and the template can do the same. This one does not — the picture is a
// portrait box in the middle of a white canvas with a border around it, and
// the overlay (bar, goals table, match-up lockup) sits on the white around it.
//
// The frame therefore has to be described in ONE place that both the editor
// canvas and the template render from, or the two drift apart the moment
// either is nudged. It is stored as fractions of the canvas rather than pixels
// because the two draw at very different sizes -- the template at its real
// 1200x1500, the editor at whatever getWrapperDimensions() hands back (490x613
// for 4:5) -- and a fraction is the same box in both.
//
// The layers themselves are untouched by this: they keep their own dimensions,
// x/y and adjustment filters, and are simply clipped by the frame, so a layer
// starts out filling the frame and can be dragged and resized behind it like a
// photo behind a window mount.

export const SLIDER2_TEMPLATE = "fcb_matchday_slider_2";

export const isSlider2Template = (templateName) => templateName === SLIDER2_TEMPLATE;

// The size the template is authored at (`.wc-content` in style/style.scss).
// The frame is worked out in these pixels and then stored as fractions, so it
// lands on the same spot whatever size it is actually drawn at.
const CANVAS_HEIGHT = 1500;

// Where the stats bar's column begins on screen, and so where the picture
// begins too. `.match-stats-container-wrap` is laid out as a horizontal
// 1100x108 bar and then rotated -90deg about its top-right corner, which turns
// it into a column running DOWN from that corner -- so its CSS `top: 270px` is
// the line the column starts on, and matching it here puts the two on a level.
// If that rule moves, move this with it.
const STATS_BAR_TOP = 270;

// Where the picture stops. The design's frame is 56.9% of the canvas tall
// (measured from the PSD), hung from the top above: 270 + 0.569 * 1500 = 1123.5.
const FRAME_BOTTOM = STATS_BAR_TOP + 0.569 * CANVAS_HEIGHT;

// Fractions of the canvas. `left` is set rather than derived: the box is not
// quite centred but sits a little right of centre -- dead centre would be
// (1 - width) / 2, or 0.275 -- to leave more of the white margin on the side
// the goals table runs down.
export const SLIDER2_FRAME = {
  width: 0.45,
  left: 0.325,
  top: STATS_BAR_TOP / CANVAS_HEIGHT,
  height: (FRAME_BOTTOM - STATS_BAR_TOP) / CANVAS_HEIGHT,
};

// The bevel cut off the top-right and bottom-left corners, and the width of
// the edge drawn around the box -- both in template pixels, both scaled down
// by the caller when the box is drawn smaller. 45deg cuts, like the rest of
// this template's (`.base-time:before`, `.media1`).
export const SLIDER2_FRAME_CORNER = 40;
export const SLIDER2_FRAME_BORDER = 3;

// A CSS `border` cannot draw this edge. `clip-path` clips the element's whole
// rendered box, border included: the border is painted around the rectangular
// border-box and the polygon then slices the corners off and takes the border
// with them, leaving the diagonals bare. There is no edge there for a border
// to sit on -- the diagonal is a cut, not a side.
//
// So the edge is a SHAPE instead. The frame holds a plate filled with the
// border colour and clipped to the outer polygon, and the picture is clipped
// to the same polygon inset by the border width; the plate shows through the
// difference as an even edge that follows the bevels. This is the same trick
// `.base-time:before` uses in style.scss.
//
// Insetting a 45deg diagonal by w slides its endpoints along the straight
// edges by w * (sqrt(2) - 1) -- the correction that rule spells out as its
// 0.83px for a 2px edge.
const DIAGONAL_INSET = Math.SQRT2 - 1;

// Rounded, or the diagonal correction below puts seventeen decimal places
// into every one of these in the DOM.
const px = (value) => `${Math.round(value * 100) / 100}px`;

const clipPolygon = (points) =>
  `polygon(${points.map(([x, y]) => `${x} ${y}`).join(", ")})`;

// The frame's outline: square top-left and bottom-right, cut across the other
// two corners.
const outerPoints = (scale) => {
  const c = SLIDER2_FRAME_CORNER * scale;
  return [
    ["0", "0"],
    [`calc(100% - ${px(c)})`, "0"],
    ["100%", px(c)],
    ["100%", "100%"],
    [px(c), "100%"],
    ["0", `calc(100% - ${px(c)})`],
  ];
};

// The same outline pulled in by the border width, which is the area the
// picture is allowed to fill.
const picturePoints = (scale) => {
  const c = SLIDER2_FRAME_CORNER * scale;
  const b = SLIDER2_FRAME_BORDER * scale;
  const d = c + b * DIAGONAL_INSET;
  return [
    [px(b), px(b)],
    [`calc(100% - ${px(d)})`, px(b)],
    [`calc(100% - ${px(b)})`, px(d)],
    [`calc(100% - ${px(b)})`, `calc(100% - ${px(b)})`],
    [px(d), `calc(100% - ${px(b)})`],
    [px(b), `calc(100% - ${px(d)})`],
  ];
};

export const getSlider2OuterClip = (scale = 1) => clipPolygon(outerPoints(scale));

export const getSlider2PictureClip = (scale = 1) => clipPolygon(picturePoints(scale));

// Just the edge: the outer outline with the picture's cut out of it, as one
// even-odd polygon (outer loop, then inner loop, each closed back on its
// start). The editor lays this over its layers, where the plate underneath
// would otherwise be hidden by the layers' drag-handle outlines.
export const getSlider2EdgeClip = (scale = 1) => {
  const outer = outerPoints(scale);
  const inner = picturePoints(scale);
  const loop = [...outer, outer[0], ...inner, inner[0]];
  return `polygon(evenodd, ${loop.map(([x, y]) => `${x} ${y}`).join(", ")})`;
};

// How far the plate and the picture box both sit in from the frame, in
// template pixels. Scaled like everything else here: unscaled, the editor's
// ~0.41x canvas pulled the plate in 2 screen px (~5 template px) while the
// picture over it was cut in only 1.2, so the picture covered the plate and
// the edge all but vanished in edit mode.
export const SLIDER2_FRAME_INSET = 2;

// The plate that shows through as the edge. A child rather than a background
// on the frame itself, because the editor cannot clip the frame -- see below.
export const getSlider2PlateStyle = (scale = 1) => ({
  position: "absolute",
  inset: SLIDER2_FRAME_INSET * scale,
  clipPath: getSlider2OuterClip(scale),
});

// What the picture is drawn into: the frame inset like the plate, then cut by
// the border width. The template wraps its layers in this; the editor puts the
// same style on each layer's own clipping box instead, since wrapping them
// would take the drag handles with it. Either way it is also the box the
// layers' x/y count from, so both need the same inset or a photo shifts
// between edit mode and the export.
export const getSlider2PictureStyle = (scale = 1) => ({
  position: "absolute",
  inset: SLIDER2_FRAME_INSET * scale,
  clipPath: getSlider2PictureClip(scale),
});

// The box the layers are drawn into. It is the containing block they position
// themselves against, so a layer at x/y 0,0 sits in the frame's top-left
// corner rather than the canvas's -- which is what lets one set of saved
// coordinates mean the same thing in the editor and in the export.
//
// `clip` is what makes an oversized or dragged-off layer read as a crop
// instead of spilling across the canvas. The template wants it: it paints each
// layer as a bare positioned div with nothing else to trim it. The editor does
// NOT: it already clips each layer inside its own frame-sized box, and it
// hangs the layer's drag/resize handles outside that box on purpose. Clipping
// there as well would swallow the handles of any layer zoomed past the frame
// and leave it impossible to grab.
export const getSlider2FrameStyle = ({ clip = true } = {}) => ({
  position: "absolute",
  left: `${SLIDER2_FRAME.left * 100}%`,
  top: `${SLIDER2_FRAME.top * 100}%`,
  width: `${SLIDER2_FRAME.width * 100}%`,
  height: `${SLIDER2_FRAME.height * 100}%`,
  ...(clip ? { overflow: "hidden" } : null),
  boxSizing: "border-box",
});

// The size a fresh layer should be so that it exactly fills the frame, given
// the canvas the editor is currently working at. Anything wider or taller than
// this is cropped by the frame, which is the intended way to zoom in.
export const getSlider2FrameSize = (canvasDimensions) => ({
  width: canvasDimensions.width * SLIDER2_FRAME.width,
  height: canvasDimensions.height * SLIDER2_FRAME.height,
});
